import {
  ArdoqId,
  ViewIds,
  type APIComponentAttributes,
  type APIReferenceAttributes,
} from '@ardoq/api-types';
import { Locale, localeCompareNumericLowercase } from '@ardoq/locale';
import {
  NumericSortOrder,
  SortAttribute,
  comparePrimitiveValuesOrDates,
} from '@ardoq/common-helpers';
import { referenceInterface } from '@ardoq/reference-interface';
import {
  emptyState,
  extractCustomFieldsFromData,
  fieldHeaderProps,
  getCellValue,
  referencePropsFromData,
  setupHeaders,
  type TableCustomFieldContext,
} from './common/utils';
import { orgUsers$ } from 'streams/orgUsers/orgUsers$';
import { documentArchiveAttachments$ } from 'streams/documentArchive/documentArchiveAttachments$';
import {
  resetExpandedDescriptionsReducer,
  toggleDescriptionReducer,
} from './common/commonReducers';

import { loadVisualizationSlideSuccess } from 'presentation/viewPane/actions';
import { notifyFiltersChanged } from 'streams/filters/FilterActions';
import { references$ } from 'streams/references/references$';
import { components$, ComponentsState } from 'streams/components/components$';
import { fields$, FieldsState, toFieldTypeKey } from 'streams/fields/fields$';
import { componentInterface } from '@ardoq/component-interface';
import {
  CellTypes,
  HeaderModel,
  TableViewModel,
  TableViewRow,
} from './common/types';
import { debounceTime, startWith } from 'rxjs/operators';
import { startAction } from 'actions/utils';
import {
  action$,
  streamReducer,
  ofType,
  reduceState,
  withNamespace,
} from '@ardoq/rxbeach';
import * as profiling from '@ardoq/profiling';
import { Observable, combineLatest } from 'rxjs';
import type { ContextShape } from '@ardoq/data-model';
import { context$ } from 'streams/context/context$';
import { ContextSort } from '@ardoq/common-helpers';
import {
  getContextComponentChildren,
  relevantComponentsFromContextShape,
} from 'tabview/componentReducers';
import { APIFieldAttributes } from '@ardoq/api-types/';
import { dateRangeOperations } from '@ardoq/date-range';
import { uniqBy } from 'lodash';
import { referenceOperations } from '@ardoq/reference-operations';
import type { StreamCollection } from 'streams/utils/streamUtils';
import { ExtractStreamShape } from 'tabview/types';
import { locale$ } from 'streams/locale$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { t, type I18nLocale } from '@ardoq/i18n';

export const getDefaultHeaders = (i18nLocale: I18nLocale): HeaderModel[] => [
  {
    key: 'id',
    label: t(i18nLocale, 'tableViews:reference.ardoqOid', 'Ardoq OID'),
    cellType: CellTypes.TEXT,
    isExpandController: false,
  },
  {
    key: 'source',
    label: t(
      i18nLocale,
      'tableViews:reference.sourceComponent',
      'Source Component'
    ),
    cellType: CellTypes.NAME,
    order: null,
    isExpandController: false,
  },
  {
    key: 'type',
    label: t(i18nLocale, 'tableViews:reference.type', 'Type'),
    cellType: CellTypes.TEXT,
    order: null,
    isExpandController: false,
  },
  {
    key: 'target',
    label: t(
      i18nLocale,
      'tableViews:reference.targetComponent',
      'Target Component'
    ),
    cellType: CellTypes.NAME,
    order: null,
    isExpandController: false,
  },
  {
    key: 'displayText',
    label: t(i18nLocale, 'tableViews:reference.displayText', 'Display text'),
    cellType: CellTypes.TEXT,
    order: null,
    isExpandController: true,
  },
  {
    key: 'created',
    label: t(i18nLocale, 'tableViews:reference.created', 'Created'),
    cellType: CellTypes.DATE_TIME,
    isExpandController: false,
  },
  {
    key: 'last-updated',
    label: t(i18nLocale, 'tableViews:reference.lastUpdated', 'Last updated'),
    cellType: CellTypes.DATE_TIME,
    isExpandController: false,
  },
];
const toRow =
  (
    fields: APIFieldAttributes[],
    componentsById: Record<ArdoqId, APIComponentAttributes>,
    customFieldContext: TableCustomFieldContext
  ) =>
  (reference: APIReferenceAttributes): TableViewRow => {
    const props = referencePropsFromData(
      reference,
      componentsById,
      referenceInterface.getReferenceTypeNameByReferenceId(reference._id)
    );

    return {
      ...props,
      ...extractCustomFieldsFromData(fields, reference, customFieldContext),
      hasDescription: Boolean(reference.description),
      markdownDescription: reference.description || '',
      expandDescription: false,
    };
  };

const getChildComponentReferences = (
  componentId: string,
  references: APIReferenceAttributes[],
  childrenByParent: Map<ArdoqId, APIComponentAttributes[]>
): APIReferenceAttributes[] => {
  const collect = (id: string): APIReferenceAttributes[] => [
    ...references.filter(ref => referenceOperations.isForComponent(ref, id)),
    ...(childrenByParent.get(id) ?? []).flatMap(c => collect(c._id)),
  ];

  return uniqBy(collect(componentId), '_id');
};

const getReferences = (
  context: ContextShape,
  references: APIReferenceAttributes[],
  childrenByParent: Map<ArdoqId, APIComponentAttributes[]>,
  locale: Locale
): APIReferenceAttributes[] => {
  const sortById = (a: APIReferenceAttributes, b: APIReferenceAttributes) =>
    localeCompareNumericLowercase(a._id, b._id, locale);

  if (
    context.componentId &&
    componentInterface.isComponent(context.componentId)
  ) {
    return getChildComponentReferences(
      context.componentId,
      references,
      childrenByParent
    ).sort(sortById);
  } else if (context.workspaceId) {
    return references
      .filter(ref =>
        referenceOperations.isInWorkspace(ref, context.workspaceId)
      )
      .sort(sortById);
  }
  return [];
};

type NextStateArgs = {
  references: APIReferenceAttributes[];
  fields: FieldsState;
  locale: Locale;
  i18nLocale: I18nLocale;
  sort: ContextSort;
  hasComponentsAvailable: boolean;
  componentsById: Record<ArdoqId, APIComponentAttributes>;
  customFieldContext: TableCustomFieldContext;
};
const nextState = ({
  locale,
  i18nLocale,
  references,
  fields,
  sort,
  hasComponentsAvailable,
  componentsById,
  customFieldContext,
}: NextStateArgs) => {
  if (references.length === 0) {
    return { ...emptyState, rows: [], hasComponentsAvailable };
  }

  const filteredReferences = references.filter(ref =>
    referenceInterface.isIncludedInContextByFilter(ref)
  );

  const referenceFields = filteredReferences.flatMap(
    ref => fields.byReferenceType.get(toFieldTypeKey(ref.model, ref.type)) ?? []
  );

  const customFields = uniqBy(referenceFields, '_id')
    .filter(field => field && field.label && field.name)
    .sort((a, b) => localeCompareNumericLowercase(a.label, b.label, locale));

  // Date range fields are formatted as comma-separated strings (start,end) for use in the sorting logic.
  const customFieldsToPresent = dateRangeOperations
    .mergeDateTimeFieldsToDateRangeFields(customFields)
    .fields.map(field =>
      dateRangeOperations.isDateRangeField(field)
        ? {
            ...field,
            name: `${field.dateTimeFields.start.name},${field.dateTimeFields.end.name}`,
          }
        : field
    )
    .filter(field => field && field.label && field.name);

  const sortRows = (
    rows: TableViewRow[],
    sort: ContextSort,
    locale: Locale
  ): TableViewRow[] => {
    if (sort.attr === SortAttribute.ORDER) return rows;

    return [...rows].sort((a, b) => {
      const getValue = (row: TableViewRow) => {
        if (sort.attr === SortAttribute.SOURCE) {
          return row.source?.name;
        }
        if (sort.attr === SortAttribute.TARGET) {
          return row.target?.name;
        }
        const rawValue = row[sort.attr];
        return getCellValue(rawValue);
      };

      const aValue = getValue(a);
      const bValue = getValue(b);

      if (aValue === bValue || (!aValue && !bValue)) {
        return localeCompareNumericLowercase(
          a.displayText || '',
          b.displayText || '',
          locale
        );
      }

      return (
        comparePrimitiveValuesOrDates(aValue, bValue, locale) *
        (sort.order === NumericSortOrder.DESC ? -1 : 1)
      );
    });
  };

  const rows = filteredReferences.map(
    toRow(customFieldsToPresent, componentsById, customFieldContext)
  );
  const sortedRows = sortRows(rows, sort, locale);

  const processedDefaultHeaders = setupHeaders(
    sort,
    getDefaultHeaders(i18nLocale)
  ).filter(header => header && header.label);
  const processedFieldHeaders = setupHeaders(
    sort,
    uniqBy(customFieldsToPresent, 'name').map(fieldHeaderProps)
  ).filter(header => header && header.label);
  const processedReferenceTypeHeaders: HeaderModel[] = [];

  return {
    ...emptyState,
    hasComponentsAvailable,
    allDefaultHeaders: processedDefaultHeaders,
    allReferenceTypeHeaders: processedReferenceTypeHeaders,
    allFieldHeaders: processedFieldHeaders,
    defaultHeaders: processedDefaultHeaders,
    fieldHeaders: processedFieldHeaders,
    referenceTypeHeaders: processedReferenceTypeHeaders,
    rows: sortedRows,
  };
};

export const reset = (
  context: ContextShape,
  references: APIReferenceAttributes[],
  fields: FieldsState,
  locale: Locale,
  components: ComponentsState,
  i18nLocale: I18nLocale,
  customFieldContext: TableCustomFieldContext
): TableViewModel => {
  const transaction = profiling.startTransaction(
    'reference table view model calculation',
    500,
    profiling.Team.INSIGHT
  );
  const includedComponentIds = new Set(
    getContextComponentChildren(context, components)
      .filter(component =>
        componentInterface.isIncludedInContextByFilter(component)
      )
      .map(({ _id }) => _id)
  );
  const { hasComponentsAvailable } = relevantComponentsFromContextShape({
    context,
    components,
    locale,
    includedComponentIds,
  });
  const state = nextState({
    references: getReferences(
      context,
      references,
      components.childrenByParent,
      locale
    ),
    fields,
    locale,
    i18nLocale,
    sort: context.sort,
    hasComponentsAvailable,
    componentsById: components.byId,
    customFieldContext,
  });
  profiling.endTransaction(transaction, { viewId: ViewIds.REFERENCETABLE });
  return state;
};

const resetNotifier$ = combineLatest([
  context$,
  references$,
  locale$,
  fields$,
  components$,
  i18nLocale$,
  orgUsers$,
  documentArchiveAttachments$,
  action$.pipe(
    ofType(notifyFiltersChanged, loadVisualizationSlideSuccess),
    startWith(startAction())
  ),
]).pipe(debounceTime(50));

const resetOnChanges = (
  _: TableViewModel,
  [
    context,
    { models },
    locale,
    fields,
    components,
    i18nLocale,
    orgUsers,
    attachments,
  ]: [
    ContextShape,
    StreamCollection<APIReferenceAttributes>,
    Locale,
    FieldsState,
    ComponentsState,
    I18nLocale,
    ExtractStreamShape<typeof orgUsers$>,
    ExtractStreamShape<typeof documentArchiveAttachments$>,
    unknown,
  ]
) =>
  reset(context, models, fields, locale, components, i18nLocale, {
    usersById: orgUsers.byId,
    attachmentsById: attachments.byId,
    i18nLocale,
  });

export const createViewModel$ = (
  viewInstanceId: string
): Observable<TableViewModel> => {
  return action$.pipe(
    withNamespace(viewInstanceId),
    reduceState('referenceTableViewModel$', emptyState, [
      resetExpandedDescriptionsReducer(ViewIds.REFERENCETABLE),
      toggleDescriptionReducer,
      streamReducer(resetNotifier$, resetOnChanges),
    ]),
    debounceTime(50)
  );
};
